import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { useLockingEnabled } from '@/tools/models/useLockingEnabled.hook';
import { faArrowUp, faLock } from '@seeqdev/icons/fa-sharp-regular-svg-icons';
import { Icon } from '@seeqdev/qomponents';
import { colors } from '@seeqdev/qomponents/colors.stylex';
import type React from 'react';
import type { ApplicationVersionInfo } from '@/tools/models/useTableTemplateVersionMetadata.hook';

interface TemplateColumnHeaderProps {
  /** Template name supplied by ag-grid as the column-group header text. */
  displayName: string;
  versionInfo?: ApplicationVersionInfo;
  /** Invoked when the user activates the upgrade badge to re-apply the production version. */
  onUpgrade?: () => void;
}

/**
 * ag-grid column-group header for template columns in a Scaling Table. Renders the template
 * name plus a "v{N}" badge when Locking is enabled and the application's referenced
 * version is known. When the application is outdated and an `onUpgrade` callback is
 * provided, also renders a clickable upgrade badge showing the production version.
 */
export const TemplateColumnHeader = ({ displayName, versionInfo, onUpgrade }: TemplateColumnHeaderProps) => {
  const { t } = useTranslation();
  const lockingEnabled = useLockingEnabled();
  const referencedVersionNumber = versionInfo?.referencedVersionNumber;
  const productionVersionNumber = versionInfo?.productionVersionNumber;
  const outdated =
    referencedVersionNumber !== undefined &&
    productionVersionNumber !== undefined &&
    productionVersionNumber > referencedVersionNumber;

  const handleUpgrade = (event: React.SyntheticEvent) => {
    // ag-grid attaches click handlers to column headers for sorting/menus; stop propagation so the upgrade action stays isolated.
    event.stopPropagation();
    onUpgrade?.();
  };

  return (
    <span
      data-testid="templateColumnHeader"
      {...stylex.props(styles.columnHeader)}
      className={`flexColumnContainer ${stylex.props(styles.columnHeader).className}`}>
      <span>{displayName}</span>
      {lockingEnabled && referencedVersionNumber !== undefined && (
        <span {...stylex.props(styles.badge)} data-testid="templateColumnVersionLabel">
          <Icon icon={faLock} type="theme" xstyle={styles.badgeIcon} />
          {t('TEMPLATES.VERSION_LABEL', { version: referencedVersionNumber })}
        </span>
      )}
      {outdated && onUpgrade !== undefined && (
        <span
          {...stylex.props(styles.badge, styles.interactive)}
          data-testid="templateColumnUpgradeBadge"
          role="button"
          tabIndex={0}
          onClick={handleUpgrade}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleUpgrade(event);
            }
          }}>
          <Icon icon={faArrowUp} type="theme" xstyle={styles.badgeIcon} />
          {lockingEnabled
            ? t('TEMPLATES.UPGRADE_TO_PRODUCTION', { version: productionVersionNumber })
            : t('TEMPLATES.UPGRADE_AVAILABLE')}
        </span>
      )}
    </span>
  );
};

const styles = stylex.create({
  badge: {
    paddingLeft: '.375rem',
    paddingRight: '.375rem',
    paddingTop: '.125rem',
    paddingBottom: '.125rem',
    fontSize: '.7rem',
    fontWeight: 600,
    borderRadius: '.25rem',
    whiteSpace: 'nowrap',
    backgroundColor: colors.toolThemeLight,
    color: colors.toolThemeDark,
    marginLeft: '.25rem',
  },
  interactive: { cursor: 'pointer' },
  columnHeader: { display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' },
  badgeIcon: { marginRight: '0.125rem', fontSize: '0.625rem' },
});
